import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ServicePackageOrder } from '../models/service-package-order.model';
import { ServicePackageOrderFilter } from '../models/dtos/service-package-order-filter';
import { BoatWithPackagesDTO } from '../models/boat-with-packages.model';

@Injectable({
  providedIn: 'root'
})
export class ServicePackageOrderService {
  private baseUrl = environment.apiHost + '/api-core/secure/package-service';

  constructor(private http: HttpClient) { }

  findAll(): Observable<ServicePackageOrder[]> {
    return this.http.get<ServicePackageOrder[]>(this.baseUrl);
  }

  findById(id: number): Observable<ServicePackageOrder> {
    return this.http.get<ServicePackageOrder>(`${this.baseUrl}/${id}`);
  }

  findByMarinaId(marinaId: number): Observable<ServicePackageOrder[]> {
    return this.http.get<ServicePackageOrder[]>(`${this.baseUrl}/marina/${marinaId}`);
  }

  findByMarinaIdWithPagination(filter: ServicePackageOrderFilter): Observable<any> {
    let params = new HttpParams()
      .set('page', filter.page.toString())
      .set('size', filter.size.toString())
      .set('order', filter.order);

    if (filter.marinaId) {
      params = params.set('marinaId', filter.marinaId.toString());
    }
    if (filter.boatId) {
      params = params.set('boatId', filter.boatId.toString());
    }
    if (filter.customerId) {
      params = params.set('customerId', filter.customerId.toString());
    }
    if (filter.packageServiceId) {
      params = params.set('packageServiceId', filter.packageServiceId.toString());
    }
    if (filter.startDate) {
      params = params.set('startDate', filter.startDate.toISOString());
    }
    if (filter.endDate) {
      params = params.set('endDate', filter.endDate.toISOString());
    }
    if (filter.packageStatus && filter.packageStatus.length > 0) {
      params = params.set('packageStatus', filter.packageStatus.join(','));
    }

    return this.http.get(`${this.baseUrl}/filter`, { params })
      .pipe(map((response: any) => {
        return {
          content: response.content as BoatWithPackagesDTO[],
          totalElements: response.totalElements
        };
      }));
  }
  
  findBoatsWithPackages(filter: ServicePackageOrderFilter): Observable<any> {
    let params = new HttpParams()
      .set('page', filter.page.toString())
      .set('size', filter.size.toString())
      .set('order', filter.order);

    if (filter.marinaId) {
      params = params.set('marinaId', filter.marinaId.toString());
    }
    if (filter.boatId) {
      params = params.set('boatId', filter.boatId.toString());
    }
    if (filter.customerId) {
      params = params.set('customerId', filter.customerId.toString());
    }

    return this.http.get(`${this.baseUrl}/boats-with-packages`, { params })
      .pipe(map((response: any) => {
        return {
          content: response.content as BoatWithPackagesDTO[],
          totalElements: response.totalElements
        };
      }));
  }


  create(servicePackageOrder: ServicePackageOrder): Observable<ServicePackageOrder> {
    return this.http.post<ServicePackageOrder>(this.baseUrl, servicePackageOrder);
  }

  update(id: number, servicePackageOrder: ServicePackageOrder): Observable<ServicePackageOrder> {
    return this.http.put<ServicePackageOrder>(`${this.baseUrl}/${id}`, servicePackageOrder);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  updateValidityEndDate(packageBoatId: number, validityEndDate: Date): Observable<void> {
    const body = {
      validityEndDate: validityEndDate.toISOString().split('T')[0] + 'T23:59:59'
    };
    return this.http.put<void>(`${this.baseUrl}/package-boat/${packageBoatId}/validity-end-date`, body);
  }

  cancelPackageBoat(packageBoatId: number): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/package-boat/${packageBoatId}/cancel`, {});
  }
}